import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import Avatar from 'components/common/Avatar';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import draftEntryUiHelper from 'helpers/draft-entry-ui-helper';
import userHelper from 'helpers/userHelper';
import Colors from 'util/Colors';
import c from 'util/const';
import filters from 'util/filters';

export const getEntryDateTime = entry =>
  entry?.SigningTimestamp || entry?.Completed || entry?.Created || null;

const buildSharedByColumn = onRowClicked => ({
  accessor: 'NotaryFullName',
  Header: 'Shared by',
  headerProps: { className: 'pe-1' },
  cellProps: { className: 'py-2' },
  Cell: rowData => {
    const { NotaryFullName, NotaryEmail, Stricken } = rowData.row.original;
    const name = NotaryFullName?.trim() || NotaryEmail?.trim() || 'Unknown';
    return (
      <Flex
        alignItems="center"
        className="cursor-pointer"
        onClick={() => onRowClicked(rowData.row.original)}
      >
        <Avatar
          size="xl"
          name={name}
          className="me-2"
          mediaClass={Stricken ? 'bg-danger' : 'bg-soft-primary text-primary'}
        />
        <div className="flex-1 lh-sm">
          <h5
            className={`mb-0 fs--1${Stricken ? ' text-danger' : ''}`}
            style={Stricken ? { textDecoration: 'line-through' } : {}}
          >
            {name}
          </h5>
          {NotaryEmail && NotaryEmail !== name ? (
            <div className="fs--2 text-600">{NotaryEmail}</div>
          ) : null}
        </div>
      </Flex>
    );
  }
});

export const buildRepositoryNotaryJournalColumns = ({
  onRowClicked,
  privacyEnabled = false,
  showNotary = false
}) => [
  {
    accessor: 'EmailSent',
    Header: 'Mail',
    headerProps: { className: 'pe-1' },
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { EmailSent, LocalTimezone } = rowData.row.original;
      return (
        <Flex alignItems="center">
          {EmailSent ? (
            <FontAwesomeIcon
              size="xl"
              color={Colors.success.color}
              icon="envelope"
              className="me-2"
              title={`Sent mail on: ${draftEntryUiHelper
                .getDateTimeWithTimeZone(
                  EmailSent,
                  userHelper.getTimezoneVal(LocalTimezone)
                )
                .format(c.dateFormat)}`}
            />
          ) : (
            <FontAwesomeIcon size="xl" icon="envelope" className="me-2" />
          )}
        </Flex>
      );
    }
  },
  {
    accessor: 'SigningTimestamp',
    Header: 'Notarization Date and Time',
    Cell: rowData => {
      const { Stricken, LocalTimezone } = rowData.row.original;
      const entryDateTime = getEntryDateTime(rowData.row.original);
      const formattedDate = entryDateTime
        ? draftEntryUiHelper
            .getDateTimeWithTimeZone(
              entryDateTime,
              userHelper.getTimezoneVal(LocalTimezone)
            )
            .format(c.dateFormat)
        : '-';
      return (
        <span
          className={Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'}
          onClick={() => onRowClicked(rowData.row.original)}
          style={Stricken ? { textDecoration: 'line-through' } : {}}
        >
          {formattedDate}
        </span>
      );
    }
  },
  ...(showNotary ? [buildSharedByColumn(onRowClicked)] : []),
  {
    accessor: 'Signers',
    Header: 'Signers',
    headerProps: { className: 'pe-1' },
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { Signers, Stricken } = rowData.row.original;
      const signerName = Signers ? filters.buildSignerString(Signers) : '';
      return signerName && signerName.length > 0 ? (
        <Flex
          alignItems="center"
          onClick={() => onRowClicked(rowData.row.original)}
        >
          <Avatar
            size="xl"
            name={signerName.substring(0, 10)}
            className="me-2"
            mediaClass={
              Stricken ? 'cursor-pointer bg-danger' : 'cursor-pointer'
            }
          />
          <div className="flex-1">
            <h5
              className={
                Stricken
                  ? 'mb-0 fs--1 cursor-pointer text-danger'
                  : 'mb-0 fs--1 cursor-pointer'
              }
              style={Stricken ? { textDecoration: 'line-through' } : {}}
            >
              {privacyEnabled
                ? signerName.replace(/[a-zA-Z0-9]/g, '*').substring(0, 10)
                : signerName}
            </h5>
          </div>
        </Flex>
      ) : (
        <h5
          className={
            Stricken
              ? 'mb-0 fs--1 ms-4 cursor-pointer text-danger'
              : 'mb-0 fs--1 ms-4 cursor-pointer'
          }
          style={Stricken ? { textDecoration: 'line-through' } : {}}
        >
          -
        </h5>
      );
    }
  },
  {
    accessor: 'NotaryEntries',
    Header: 'Entries',
    Cell: rowData => {
      const { NotaryEntries, Stricken } = rowData.row.original;
      return (
        <span
          className={Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'}
          style={Stricken ? { textDecoration: 'line-through' } : {}}
          onClick={() => onRowClicked(rowData.row.original)}
        >
          {NotaryEntries?.length || 0} Document
          {NotaryEntries?.length === 1 ? '' : 's'}
        </span>
      );
    }
  },
  {
    accessor: 'NotaryFee',
    Header: 'Notary Fee',
    Cell: rowData => {
      const { NotaryFee, NotaryFeeCurrency, Stricken } = rowData.row.original;
      return (
        <span
          className={Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'}
          style={Stricken ? { textDecoration: 'line-through' } : {}}
          onClick={() => onRowClicked(rowData.row.original)}
        >
          {NotaryFee === 0
            ? '$0'
            : NotaryFee
            ? privacyEnabled
              ? '****'
              : filters.formatCurrencyFromServer(NotaryFee, NotaryFeeCurrency)
            : '- None entered -'}
        </span>
      );
    }
  },
  {
    accessor: 'State',
    Header: 'Address',
    Cell: rowData => {
      const { State, PostalCode, Stricken } = rowData.row.original;
      return (
        <span
          className={Stricken ? 'text-danger' : ''}
          style={Stricken ? { textDecoration: 'line-through' } : {}}
        >
          {State} - {PostalCode}
        </span>
      );
    }
  },
  {
    accessor: 'none',
    Header: '',
    disableSortBy: true,
    cellProps: { className: 'text-end' },
    Cell: rowData => (
      <CardDropdown drop="start" iconClassName="fs--1">
        <div className="py-2">
          <Dropdown.Item
            href="#!"
            onClick={() => onRowClicked(rowData.row.original)}
          >
            <FontAwesomeIcon icon={faEye} className="me-2" fixedWidth />
            View
          </Dropdown.Item>
        </div>
      </CardDropdown>
    )
  }
];
